import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, Cell
} from "recharts";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "../css/StatisticsPage.css";

const financialData = [
  { year: "2000", revenue: 3900, assets: 4375, shareholders: 1250, ncl: 625, cl: 2000 },
  { year: "2001", revenue: 5000, assets: 4375, shareholders: 1375, ncl: 625, cl: 1875 },
  { year: "2002", revenue: 4375, assets: 4875, shareholders: 1750, ncl: 750, cl: 2500 },
  { year: "2003", revenue: 4375, assets: 3250, shareholders: 1375, ncl: 750, cl: 2500 },
  { year: "2004", revenue: 4875, assets: 3875, shareholders: 1875, ncl: 625, cl: 1875 },
  { year: "2005", revenue: 5625, assets: 5125, shareholders: 1875, ncl: 625, cl: 2625 },
  { year: "2006", revenue: 8500, assets: 7125, shareholders: 2625, ncl: 625, cl: 2625 },
  { year: "2007", revenue: 8621, assets: 11572, shareholders: 3174, ncl: 749, cl: 7641 },
  { year: "2008", revenue: 13125, assets: 12500, shareholders: 4000, ncl: 750, cl: 7750 },
  { year: "2009", revenue: 13750, assets: 13750, shareholders: 4500, ncl: 750, cl: 8500 },
  { year: "2010", revenue: 11875, assets: 13625, shareholders: 5000, ncl: 750, cl: 7875 },
  { year: "2011", revenue: 15625, assets: 15625, shareholders: 8500, ncl: 875, cl: 6250 },
  { year: "2012", revenue: 15625, assets: 17500, shareholders: 10000, ncl: 875, cl: 6625 },
  { year: "2013", revenue: 16250, assets: 16250, shareholders: 10375, ncl: 625, cl: 5250 },
  { year: "2014", revenue: 13750, assets: 16250, shareholders: 10375, ncl: 625, cl: 5250 },
  { year: "2015", revenue: 13750, assets: 16875, shareholders: 9375, ncl: 625, cl: 6875 },
  { year: "2016", revenue: 10000, assets: 19250, shareholders: 9375, ncl: 875, cl: 8750 },
];

const dwtData = [
  { year: "1998", dwt: 2042236 },
  { year: "1999", dwt: 2556423 },
  { year: "2000", dwt: 1917718 },
  { year: "2001", dwt: 1838504 },
  { year: "2002", dwt: 3141777 },
  { year: "2003", dwt: 2738859 },
  { year: "2004", dwt: 1935523 },
  { year: "2005", dwt: 2289042 },
  { year: "2006", dwt: 2362818 },
  { year: "2007", dwt: 2062404 },
  { year: "2008", dwt: 1955118 },
  { year: "2009", dwt: 1616275 },
  { year: "2010", dwt: 1714751 },
  { year: "2011", dwt: 1723556 },
  { year: "2012", dwt: 1426705 },
  { year: "2013", dwt: 2221300 },
  { year: "2014", dwt: 2303124 },
  { year: "2015", dwt: 2233704 },
  { year: "2016", dwt: 2854574 },
];

const financeTicks = Array.from({ length: 33 }, (_, i) => i * 625);
const dwtTicks = Array.from({ length: 33 }, (_, i) => i * 125000);

const FinanceTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div style={{
      background: "#1a1a1a",
      color: "#fff",
      padding: "10px 14px",
      fontSize: 12,
      borderRadius: 3,
      boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
      lineHeight: 1.8
    }}>
      <p style={{ fontWeight: 700, marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: "1px 0" }}>
          <span style={{
            display: "inline-block",
            width: 10,
            height: 10,
            background: p.color,
            marginRight: 8,
            verticalAlign: "middle"
          }} />
          {p.name}: <strong>{p.value.toLocaleString()}</strong>
        </p>
      ))}
    </div>
  );
};

const DwtTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div style={{
      background: "#1a1a1a",
      color: "#fff",
      padding: "8px 14px",
      fontSize: 12,
      borderRadius: 3,
      boxShadow: "0 2px 8px rgba(0,0,0,0.4)"
    }}>
      <strong>{label} : {payload[0].value.toLocaleString()}</strong>
    </div>
  );
};

const renderLegend = (props) => {
  const { payload } = props;

  return (
    <ul className="stats-chart-legend">
      {payload.map((entry, i) => (
        <li key={i}>
          <span style={{ background: entry.color }} />
          {entry.value}
        </li>
      ))}
    </ul>
  );
};

const StatisticsPage = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();

    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <>
      <Header />

      <main className="statistics-main">
        <section
          className="stats-banner"
          style={{ backgroundImage: "url('https://www.cdl.lk/wp-content/uploads/2016/02/reports1-1.jpg')" }}
        >
          <div className="banner-overlay"></div>
        </section>

        <div className="breadcrumb-container">
          <div className="container">
            <nav className="breadcrumb">
              <a href="/">Home</a>
              <span className="separator">&gt;</span>
              <span className="current">Statistics</span>
            </nav>
          </div>
        </div>

        <section className="statistics-content">
          <div className="container">
            <h1 className="main-title">STATISTICS</h1>

            <div className="chart-section">
              <h2 className="chart-title">
                SUMMARY OF FINANCIAL PERFORMANCE (RS. MN)
              </h2>

              <div className="chart-card">
                <ResponsiveContainer width="100%" height={isMobile ? 520 : 500}>
                  <BarChart
                    data={financialData}
                    margin={
                      isMobile
                        ? { top: 10, right: 5, left: 0, bottom: 70 }
                        : { top: 10, right: 180, left: 10, bottom: 5 }
                    }
                    barCategoryGap={isMobile ? "18%" : "35%"}
                    barGap={1}
                  >
                    <CartesianGrid stroke="#ddd" vertical={false} />
                    <XAxis
                      dataKey="year"
                      tick={{
                        fontSize: isMobile ? 10 : 12,
                        fill: "#555",
                        angle: isMobile ? -65 : 0,
                        textAnchor: isMobile ? "end" : "middle"
                      }}
                      height={isMobile ? 70 : 30}
                      axisLine={{ stroke: "#bbb" }}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[0, 20000]}
                      ticks={financeTicks}
                      tick={{ fontSize: isMobile ? 9 : 11, fill: "#555" }}
                      axisLine={false}
                      tickLine={false}
                      width={isMobile ? 45 : 55}
                    />
                    <Tooltip content={<FinanceTooltip />} />
                    <Legend
                      layout={isMobile ? "horizontal" : "vertical"}
                      align={isMobile ? "left" : "right"}
                      verticalAlign={isMobile ? "bottom" : "middle"}
                      content={renderLegend}
                      wrapperStyle={isMobile ? { paddingTop: 12 } : { paddingLeft: 20 }}
                    />
                    <Bar dataKey="revenue" name="Total Revenue" fill="#4a90d9" barSize={isMobile ? 5 : 8} />
                    <Bar dataKey="assets" name="Total Assets" fill="#e53935" barSize={isMobile ? 5 : 8} />
                    <Bar dataKey="shareholders" name="Shareholders Fund (Net Worth)" fill="#43a047" barSize={isMobile ? 5 : 8} />
                    <Bar dataKey="ncl" name="Non-Current Liabilities" fill="#7b1fa2" barSize={isMobile ? 5 : 8} />
                    <Bar dataKey="cl" name="Current Liabilities" fill="#00acc1" barSize={isMobile ? 5 : 8} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="chart-section">
              <h2 className="chart-title">ANNUAL DWT OF DRYDOCK REPAIRS</h2>

              <div className="chart-card">
                <ResponsiveContainer width="100%" height={isMobile ? 520 : 500}>
                  <BarChart
                    data={dwtData}
                    margin={
                      isMobile
                        ? { top: 10, right: 5, left: 0, bottom: 70 }
                        : { top: 10, right: 120, left: 10, bottom: 5 }
                    }
                    barCategoryGap={isMobile ? "20%" : "30%"}
                  >
                    <CartesianGrid stroke="#ddd" vertical={false} />
                    <XAxis
                      dataKey="year"
                      tick={{
                        fontSize: isMobile ? 10 : 12,
                        fill: "#555",
                        angle: isMobile ? -65 : 0,
                        textAnchor: isMobile ? "end" : "middle"
                      }}
                      height={isMobile ? 70 : 30}
                      axisLine={{ stroke: "#bbb" }}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[0, 4000000]}
                      ticks={dwtTicks}
                      tickFormatter={(v) => v.toLocaleString()}
                      tick={{ fontSize: isMobile ? 9 : 11, fill: "#555" }}
                      axisLine={false}
                      tickLine={false}
                      width={isMobile ? 70 : 75}
                    />
                    <Tooltip content={<DwtTooltip />} />
                    <Legend
                      layout={isMobile ? "horizontal" : "vertical"}
                      align={isMobile ? "left" : "right"}
                      verticalAlign={isMobile ? "bottom" : "middle"}
                      content={renderLegend}
                      wrapperStyle={isMobile ? { paddingTop: 12 } : { paddingLeft: 20 }}
                    />
                    <Bar dataKey="dwt" name="DWT" fill="#f05252" barSize={isMobile ? 10 : 22}>
                      {dwtData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill="#f05252" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="chart-section" style={{ marginTop: 40 }}>
              <h2 className="chart-title">
                REVENUE GENERATION AS A % – SECTOR WISE
              </h2>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default StatisticsPage;